import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { BellOff, Mail, MessageSquare, Slack, Smartphone } from 'lucide-react';
import moment from 'moment';
import styled from 'styled-components';

import {
  channelLabel,
  ILmsBlockNotification,
  ITone,
  kindLabel,
  labelText,
  notificationSkipReasonLabels,
  notificationStatusLabel,
  notificationStatusTone,
  NS,
} from 'core/utils/lmsSuspension';

interface IProps {
  notifications: ILmsBlockNotification[];
  /** Prefix each tooltip with «приостановка / возврат» (history rows). */
  showKind?: boolean;
  /**
   * `false` — the operator unticked «Powiadom studenta»: nothing was queued,
   * which is a decision, not a gap. Undefined — unknown (list rows).
   */
  notifyStudent?: boolean;
}

const TONE_COLORS: Record<ITone, { fg: string; bg: string }> = {
  ok: { fg: '#166534', bg: '#dcfce7' },
  warn: { fg: '#9a3412', bg: '#ffedd5' },
  danger: { fg: '#991b1b', bg: '#fee2e2' },
  info: { fg: '#1e40af', bg: '#dbeafe' },
  neutral: { fg: '#475569', bg: '#f1f5f9' },
};

const formatWhen = (value?: string | null): string =>
  value ? moment(value).format('DD.MM.YYYY HH:mm') : '';

/**
 * ТЗ 0074: what the student was sent about a suspension — one icon per
 * notification (email / Slack DM / SMS — ТЗ 0081), coloured by delivery status, with a
 * tooltip that names the status and WHY it was skipped or failed.
 */
export const LmsBlockNotifications = memo<IProps>(
  ({ notifications, showKind, notifyStudent }) => {
    const { t } = useTranslation();

    if (!notifications.length) {
      return notifyStudent === false ? (
        <Tooltip title={t(`${NS}.notify.notRequested`)}>
          <Pill tone="neutral" aria-label={t(`${NS}.notify.notRequested`)}>
            <BellOff size={13} strokeWidth={2} />
          </Pill>
        </Tooltip>
      ) : (
        <Dash>—</Dash>
      );
    }

    return (
      <Row>
        {notifications.map((n) => {
          const tone = notificationStatusTone(n.status);
          const channel = labelText(channelLabel(n.channel), t);
          const status = labelText(notificationStatusLabel(n.status), t);
          const reasons = notificationSkipReasonLabels(n.skipReason).map((l) =>
            labelText(l, t),
          );
          const when = formatWhen(n.sentAt || n.createdAt);
          const Icon =
            n.channel === 'email'
              ? Mail
              : n.channel === 'slack'
              ? Slack
              : n.channel === 'sms'
              ? Smartphone
              : MessageSquare;
          const title = (
            <TipBody>
              <div>
                {showKind ? `${labelText(kindLabel(n.kind), t)} · ` : ''}
                {channel}: <b>{status}</b>
                {when ? ` · ${when}` : ''}
              </div>
              {reasons.map((r) => (
                <div key={r}>{r}</div>
              ))}
              {n.error ? <div>{n.error}</div> : null}
            </TipBody>
          );
          return (
            <Tooltip key={n.id} title={title}>
              <Pill tone={tone} aria-label={`${channel}: ${status}`}>
                <Icon size={13} strokeWidth={2} />
              </Pill>
            </Tooltip>
          );
        })}
      </Row>
    );
  },
);

const Row = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Pill = styled.span<{ tone: ITone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 22px;
  border-radius: 6px;
  color: ${({ tone }) => TONE_COLORS[tone].fg};
  background: ${({ tone }) => TONE_COLORS[tone].bg};
`;

const TipBody = styled.div`
  font-size: 12px;
  line-height: 1.45;
`;

const Dash = styled.span`
  color: #94a3b8;
`;
